import React from "react";
import { createRoot } from "react-dom/client";
import html2canvas from "html2canvas";
import { Pie, Column, Bar, DualAxes } from "@ant-design/plots";
import { Renderer as SVGRenderer } from "@antv/g-svg";
import "svg2pdf.js"; // side-effect: agrega doc.svg(...) a jsPDF

// Captura con html2canvas un clon del elemento forzado a un ancho fijo, en
// vez de capturar el elemento en vivo tal cual está en pantalla. html2canvas
// tiene problemas conocidos midiendo layouts flex/grid cuando el contenedor
// vivo está en un viewport angosto o con zoom — capturar directo daba texto
// superpuesto (ej. "Próximos 30 días" pisando el subtítulo) porque el flex
// row de KPIs no tenía el ancho real que tiene en pantalla en una pantalla
// grande. Clonar a un ancho fijo generoso (1400px, mismo criterio que
// mern-consultora) le da al flex row lugar de sobra para acomodar todas las
// tarjetas en una sola línea antes de medir/capturar.
// `prepare(clone)`: hook opcional que corre sobre el CLON (nunca sobre el
// elemento en pantalla) antes de medir/capturar — para sacar del PDF
// controles que solo tienen sentido en la tabla interactiva en vivo
// (botones de Editar/Cerrar/Eliminar, inputs editables de progreso, etc.),
// que si no quedan "pegados" tal cual en la imagen estática del PDF.
export async function captureElementAtWidth(el, width = 1400, captureOpts = {}, prepare) {
  if (!el) return null;
  const wrapper = document.createElement("div");
  wrapper.style.cssText = [
    "position:absolute",
    `left:-${width + 200}px`,
    "top:0",
    `width:${width}px`,
    "overflow:visible",
    "pointer-events:none",
    "background:#fff",
  ].join(";");
  document.body.appendChild(wrapper);
  const clone = el.cloneNode(true);
  if (prepare) prepare(clone);
  wrapper.appendChild(clone);
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
  const height = Math.max(clone.scrollHeight, clone.getBoundingClientRect().height) + 8;
  try {
    return await html2canvas(clone, { scale: 2, useCORS: true, backgroundColor: "#ffffff", width, height, ...captureOpts });
  } finally {
    document.body.removeChild(wrapper);
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Renderiza gráficos de G2 (@ant-design/plots) como SVG vectorial fuera de
// pantalla y los dibuja en un PDF (jsPDF + svg2pdf.js), en vez de capturar
// los <canvas> ya pintados con html2canvas (que puede dar gráficos en
// blanco/recortados según el timing de la animación de G2). Portado del
// mismo módulo en el proyecto mern-consultora, generalizado acá porque los
// gráficos de este proyecto no siempre usan "year"/"type" como campo de eje
// (varían: mes, tipo, sitio, parte según el dashboard).
// ─────────────────────────────────────────────────────────────────────────

async function mountOffscreen(Component, config, width, height) {
  const container = document.createElement("div");
  container.style.cssText = [
    "position:absolute",
    "left:-9999px",
    "top:0",
    `width:${width}px`,
    `height:${height}px`,
    "background:#fff",
  ].join(";");
  document.body.appendChild(container);
  const root = createRoot(container);

  await new Promise((resolve) => {
    root.render(React.createElement(Component, { ...config, width, height, onReady: () => resolve() }));
  });
  // onReady dispara justo después de llamar a chart.render(), pero G2 sigue
  // reacomodando cosas de forma asíncrona después de eso (una segunda pasada
  // que mide el texto real de las etiquetas de eje). En vez de un delay fijo,
  // se espera hasta que el SVG deje de cambiar entre dos frames seguidos
  // (con un tope máximo para no colgarse si nunca se estabiliza).
  let lastSnapshot = null;
  let stableFrames = 0;
  const settleStart = Date.now();
  while (stableFrames < 2 && Date.now() - settleStart < 2000) {
    await new Promise((r) => requestAnimationFrame(r));
    const snapshot = container.querySelector("svg")?.outerHTML || "";
    if (snapshot === lastSnapshot) {
      stableFrames++;
    } else {
      stableFrames = 0;
      lastSnapshot = snapshot;
    }
  }

  const svgEl = container.querySelector("svg");
  const cleanup = () => { root.unmount(); document.body.removeChild(container); };

  // Por default el <svg> preserva su proporción al incrustarlo — lo centra
  // dentro del rectángulo destino y deja franjas en blanco a los costados en
  // vez de ocupar el ancho/alto pedidos. drawChartSection estira a propósito
  // para llenar la sección; "none" habilita ese estirado no uniforme (a
  // drawChartCard no le afecta, calcula a mano un rectángulo con la
  // proporción real).
  if (svgEl) svgEl.setAttribute("preserveAspectRatio", "none");

  // Algunos formatters de eje usan separadores/espacios Unicode (espacio
  // fino, etc.) o subíndices (ej. "CO₂") que no existen en la codificación
  // WinAnsi de las fuentes estándar de PDF — svg2pdf.js los corrompe igual
  // que le pasa a doc.text() con texto nativo. Se sanea acá, a nivel de nodo
  // <text> del SVG, para cubrir cualquier gráfico sin acordarse en cada caso.
  if (svgEl) {
    svgEl.querySelectorAll("text").forEach((node) => {
      if (node.textContent) {
        node.textContent = node.textContent
          .replace(/[   ⁠ ]/g, " ")
          .replace(/[₀-₉]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0x2080 + 0x30));
      }
    });
  }

  // El <svg> que arma G2 declara width/height/viewBox = el tamaño del
  // contenedor pedido, pero para gráficos con varias categorías el layout
  // interno a veces calcula un borde que termina un poco más allá de ese
  // viewBox (la última barra recortada al exportar). En vez de perseguir el
  // motivo exacto, se mide el bounding box real de lo dibujado y se agranda
  // el viewBox para que contenga todo el contenido.
  let naturalW = width;
  let naturalH = height;
  if (svgEl) {
    const bbox = svgEl.getBBox();
    const PAD = 2;
    const x = Math.min(0, bbox.x) - PAD;
    const y = Math.min(0, bbox.y) - PAD;
    naturalW = Math.max(width, bbox.x + bbox.width) - x + PAD;
    naturalH = Math.max(height, bbox.y + bbox.height) - y + PAD;
    svgEl.setAttribute("viewBox", `${x} ${y} ${naturalW} ${naturalH}`);
    svgEl.setAttribute("width", naturalW);
    svgEl.setAttribute("height", naturalH);
  }

  return { svgEl, cleanup, naturalW, naturalH };
}

// Las KPI cards en pantalla se separan solo por un box-shadow muy sutil
// (sin borde propio, ver KPICard.scss) — html2canvas no siempre lo capta
// con contraste suficiente, y con el fondo ahora blanco (sin el wrapper
// gris de antes) terminan pegadas unas a otras en el PDF. Se le agrega un
// borde visible a cada card en el CLON antes de capturar (nunca al
// elemento real en pantalla), vía el hook `prepare` de
// captureElementAtWidth.
export function prepareKpiCardsForPdf(clone) {
  clone.querySelectorAll(".resumen-card").forEach((card) => {
    card.style.setProperty("border", "1px solid #D5DBE3", "important");
    card.style.setProperty("box-shadow", "none", "important");
    card.style.setProperty("box-sizing", "border-box", "important");
  });
}

const CHART_PX = 220; // tamaño (cuadrado) del contenedor off-screen para renderizar la dona
// Las barras/columnas necesitan más ancho que alto: con un contenedor
// cuadrado angosto, G2 no tiene lugar para calcular las bandas de varias
// categorías más el margen del eje, y la última banda queda recortada.
const COLUMN_W = 340;
const COLUMN_H = 210;
const BAR_PLOT_W = 340;
const BAR_PLOT_H = 210;
// Los DualAxes (varias categorías/meses en el eje X) necesitan más espacio
// horizontal para no comprimir etiquetas.
const DUAL_AXES_W = 780;
const DUAL_AXES_H = 300;

export async function renderDonutForPdf(data, colorMap, { showPercentLabels = false } = {}) {
  const allData = (data || []).filter((d) => d.value > 0);
  if (allData.length === 0) return { isEmpty: true };

  const legendItems = allData.map((d) => ({ label: d.label ?? d.type, color: colorMap[d.type] || "#ccc" }));

  // Una sola categoría con 100% del total es un caso degenerado para el arco
  // SVG (ángulo inicial = ángulo final, círculo completo): el renderer SVG de
  // G2 lo dibuja como una espiral mal formada en vez de un anillo cerrado. Se
  // dibuja el anillo directo con primitivas de jsPDF en drawChartCard.
  if (allData.length === 1) {
    return {
      svgEl: null,
      cleanup: null,
      legendItems,
      singleSliceColor: colorMap[allData[0].type] || "#ccc",
      // Única categoría = 100% del total por definición — se muestra igual
      // que el resto de las donas (línea + porcentaje) en vez de omitirlo
      // solo porque no hay un arco real que etiquetar.
      singleSlicePercent: showPercentLabels ? 100 : null,
      isEmpty: false,
    };
  }

  const total = allData.reduce((acc, d) => acc + d.value, 0);
  const config = {
    data: allData,
    angleField: "value",
    colorField: "type",
    scale: { color: { domain: Object.keys(colorMap), range: Object.values(colorMap) } },
    radius: 1,
    innerRadius: 0.6,
    label: showPercentLabels
      ? {
          text: (datum) => {
            const percent = total > 0 ? (datum?.value / total) * 100 : 0;
            return percent > 0 ? `${percent.toFixed(1)}%` : "";
          },
          position: "spider",
          style: { fontSize: 8, fontWeight: 600 },
        }
      : false,
    legend: false,
    animate: false,
    renderer: new SVGRenderer(),
  };
  const { svgEl, cleanup, naturalW, naturalH } = await mountOffscreen(Pie, config, CHART_PX, CHART_PX);
  return { svgEl, cleanup, legendItems, naturalW, naturalH, isEmpty: false };
}

// Columnas verticales (G2 `Column`) — cubre tanto una serie simple coloreada
// por categoría (ej. un bar por mes/sitio, cada uno con su color) como
// varias series agrupadas/apiladas por `colorField` (ej. accidentes+días
// perdidos por mes). `xField` es parametrizable (acá varía: mes/tipo/sitio),
// a diferencia del original en mern-consultora que lo tenía fijo en "year".
export async function renderColumnForPdf(data, {
  xField, yField = "value", yLabel, colorField, colorMap, palette, fillColor,
  group = false, stack = false, maxBarWidth, width = COLUMN_W, height = COLUMN_H,
  xLabelFontSize = 9, yLabelFontSize = 9, radius,
} = {}) {
  const rows = data || [];
  if (rows.length === 0) return { isEmpty: true };

  // Ancho de barra dinámico según cantidad de categorías en el eje X: con
  // pocas (1-2) queda una barra grande y prolija; con muchas (8+) se achica
  // para que quepan sin amontonarse. Un `maxWidth` fijo (ej. 60px siempre)
  // se veía desproporcionado en ambos extremos — muy angosto con 2 barras
  // en un contenedor ancho, muy ancho/amontonado con muchas categorías.
  const categoryCount = new Set(rows.map((d) => d[xField])).size || 1;
  const effMaxBarWidth = maxBarWidth ?? Math.round(Math.max(18, Math.min(60, 130 / categoryCount)));

  // `fillColor` (sin `colorField`/`colorMap`/`group`): serie única con un
  // solo color fijo para todas las barras, sin diferenciar por categoría
  // (ej. "vencimientos por mes" de Legal Requirements/Plan Anual, donde
  // todas las barras son del mismo azul) — sin leyenda, como en pantalla.
  if (fillColor && !colorField && !group) {
    const config = {
      data: rows,
      xField,
      yField,
      style: { fill: fillColor, maxWidth: effMaxBarWidth, ...(radius !== undefined ? { radius } : {}) },
      legend: false,
      animate: false,
      axis: {
        x: { title: false, labelFontSize: xLabelFontSize },
        y: { title: yLabel || false, labelFontSize: yLabelFontSize },
      },
      renderer: new SVGRenderer(),
    };
    const { svgEl, cleanup, naturalW, naturalH } = await mountOffscreen(Column, config, width, height);
    return { svgEl, cleanup, legendItems: [], naturalW, naturalH, isEmpty: false };
  }

  const effColorField = colorField || xField;
  const keys = [...new Set(rows.map((d) => d[effColorField]))];
  const DEFAULT_PALETTE = ["#0E376E", "#1A5FA0", "#648EBC", "#3D6B9E", "#4F7FB5", "#A4C4E0", "#1A1C40", "#8AB3D4"];
  const effPalette = palette || DEFAULT_PALETTE;
  const effColorMap = colorMap || Object.fromEntries(keys.map((k, i) => [k, effPalette[i % effPalette.length]]));

  const config = {
    data: rows,
    xField,
    yField,
    colorField: effColorField,
    ...(group ? { group: true } : {}),
    ...(stack ? { stack: true } : {}),
    scale: { color: { domain: keys, range: keys.map((k) => effColorMap[k] || "#ccc") } },
    style: { maxWidth: effMaxBarWidth },
    legend: false,
    animate: false,
    axis: {
      x: { title: false, labelFontSize: xLabelFontSize },
      // G2 hace String(title) para dibujarlo — un objeto {text, style} se
      // convierte literalmente en el texto "[object Object]". El título
      // tiene que ser un string plano.
      y: { title: yLabel || false, labelFontSize: yLabelFontSize },
    },
    renderer: new SVGRenderer(),
  };
  const { svgEl, cleanup, naturalW, naturalH } = await mountOffscreen(Column, config, width, height);
  // Si no se pasó un `colorField` propio (coloreado implícito por xField,
  // solo para variar el color de cada barra), la leyenda repetiría la misma
  // etiqueta que ya está en el eje X (ej. "Ene"/"Feb"/… o "Sitio 2") sin
  // aportar nada — se omite. Con un `colorField` real y distinto (ej.
  // "estado" en un apilado por mes) sí identifica algo que el eje X no dice.
  const legendItems = colorField && colorField !== xField
    ? keys.map((k) => ({ label: String(k), color: effColorMap[k] || "#ccc" }))
    : [];
  return { svgEl, cleanup, legendItems, naturalW, naturalH, isEmpty: false };
}

// Barras horizontales (G2 `Bar`, ejes transpuestos respecto a `Column`: acá
// `xField` es el VALOR y `yField` es la CATEGORÍA) — no existe en el proyecto
// de referencia (no lo usa), hace falta para los charts horizontales de
// AccidentsDashboard (top-6 formularios, partes del cuerpo, ocupaciones).
// Serie única, un solo color fijo (no hay leyenda por categoría).
export async function renderBarPlotForPdf(data, {
  categoryField, valueField = "value", yLabel, fillColor = "#1A5FA0", maxBarWidth,
  width = BAR_PLOT_W, height = BAR_PLOT_H, labelFontSize = 9,
} = {}) {
  const rows = data || [];
  if (rows.length === 0) return { isEmpty: true };

  // Mismo criterio que renderColumnForPdf: ancho de barra dinámico según
  // cantidad de categorías, en vez de un valor fijo desproporcionado en
  // los extremos (pocas categorías muy angostas, muchas amontonadas).
  const categoryCount = new Set(rows.map((d) => d[categoryField])).size || 1;
  const effMaxBarWidth = maxBarWidth ?? Math.round(Math.max(10, Math.min(26, 80 / categoryCount)));

  const config = {
    data: rows,
    xField: valueField,
    yField: categoryField,
    style: { fill: fillColor, maxWidth: effMaxBarWidth },
    legend: false,
    animate: false,
    axis: {
      x: { title: yLabel || false, labelFontSize },
      y: { title: false, labelFontSize },
    },
    renderer: new SVGRenderer(),
  };
  const { svgEl, cleanup, naturalW, naturalH } = await mountOffscreen(Bar, config, width, height);
  return { svgEl, cleanup, legendItems: [], naturalW, naturalH, isEmpty: false };
}

// Gráfico compuesto (ej. barra+línea) — el caller arma `children` ya en el
// mismo formato que espera G2 (data compartida a nivel de componente, cada
// child solo define type/yField/style/axis), porque su composición es
// específica de cada caso de uso.
export async function renderDualAxesForPdf(data, children, { xField, scale, axis, width = DUAL_AXES_W, height = DUAL_AXES_H } = {}) {
  if (!(data || []).length) return { isEmpty: true };

  const config = {
    data,
    children,
    xField,
    scale,
    axis,
    legend: false,
    animate: false,
    renderer: new SVGRenderer(),
  };
  const { svgEl, cleanup, naturalW, naturalH } = await mountOffscreen(DualAxes, config, width, height);
  return { svgEl, cleanup, naturalW, naturalH, isEmpty: false };
}

// ── Dibujo nativo (jsPDF) ───────────────────────────────────────────────

export function hexToRgb(hex) {
  const m = (hex || "").replace("#", "").match(/.{1,2}/g);
  if (!m || m.length < 3) return [180, 180, 180];
  return m.slice(0, 3).map((h) => parseInt(h, 16));
}

// Paleta de Semantic UI (varios dashboards usan <Label color="green"|"grey"|...>
// para estados) — los helpers de dibujo nativo necesitan el hex equivalente.
export const SEMANTIC_HEX = {
  grey: "#767676", yellow: "#fbbd08", blue: "#2185d0", teal: "#00b5ad",
  green: "#21ba45", red: "#db2828", orange: "#f2711c", olive: "#b5cc18",
  violet: "#6435c9", purple: "#a333c8", pink: "#e03997", brown: "#a5673f",
  black: "#1b1c1d",
};

const PILL_H = 13;
const PILL_GAP = 6;

// Agrupa las pills en líneas (wrap) respetando maxWidth — factorizado aparte
// de drawLegendPills para poder MEDIR cuántas líneas van a hacer falta antes
// de dibujar el gráfico (ver measureLegendHeight).
function wrapLegendLines(doc, items, maxWidth) {
  doc.setFontSize(7);
  doc.setFont(undefined, "bold");
  const pillWidths = items.map((item) => doc.getTextWidth(item.label) + 16);

  const lines = [];
  let currentLine = [];
  let currentW = 0;
  items.forEach((item, i) => {
    const pillW = pillWidths[i];
    const addedW = currentLine.length ? pillW + PILL_GAP : pillW;
    if (currentLine.length && currentW + addedW > maxWidth) {
      lines.push({ items: currentLine, width: currentW });
      currentLine = [];
      currentW = 0;
    }
    currentLine.push({ item, pillW });
    currentW += currentLine.length > 1 ? pillW + PILL_GAP : pillW;
  });
  if (currentLine.length) lines.push({ items: currentLine, width: currentW });
  return lines;
}

// Alto real que va a ocupar la leyenda al dibujarse — para reservarle
// espacio antes de escalar el gráfico, en vez de una altura fija adivinada.
function measureLegendHeight(doc, items, maxWidth) {
  if (!items || !items.length) return 0;
  const lines = wrapLegendLines(doc, items, maxWidth);
  return lines.length * PILL_H + (lines.length - 1) * PILL_GAP;
}

function drawLegendPills(doc, items, x, y, maxWidth) {
  doc.setFontSize(7);
  doc.setFont(undefined, "bold");
  const lines = wrapLegendLines(doc, items, maxWidth);

  lines.forEach((line, lineIdx) => {
    let cursorX = x + (maxWidth - line.width) / 2;
    const cursorY = y + lineIdx * (PILL_H + PILL_GAP);
    line.items.forEach(({ item, pillW }) => {
      doc.setFillColor(...hexToRgb(item.color));
      doc.roundedRect(cursorX, cursorY, pillW, PILL_H, PILL_H / 2, PILL_H / 2, "F");
      doc.setTextColor(255, 255, 255);
      doc.text(item.label, cursorX + pillW / 2, cursorY + PILL_H / 2 + 2.3, { align: "center" });
      cursorX += pillW + PILL_GAP;
    });
  });

  return lines.length * PILL_H + (lines.length - 1) * PILL_GAP;
}

// Leyenda de cuadraditos de color + etiqueta — mismo estilo que
// <ChartLegend> en pantalla (caja blanca, borde gris claro, cuadradito
// redondeado + label), pero en fila (wrap a varias líneas si no entran) y
// centrada DEBAJO del gráfico, en vez de al costado — al costado competía
// por el mismo espacio horizontal que necesitan las líneas de porcentaje
// (spider labels) que salen de la dona hacia los costados. Para donas
// (áreas/categorías dinámicas); los gráficos de barra con leyenda nativa G2
// siguen con los pills de drawLegendPills.
const BOX_LEGEND_PAD_X = 10;
const BOX_LEGEND_PAD_Y = 8;
const BOX_LEGEND_ITEM_GAP = 14;
const BOX_LEGEND_ROW_GAP = 7;
const BOX_LEGEND_SWATCH = 8;
const BOX_LEGEND_ROW_H = BOX_LEGEND_SWATCH + 3;

// Agrupa los items en líneas (wrap) respetando maxWidth — mismo criterio
// que wrapLegendLines pero midiendo cuadradito+label en vez del ancho de
// una pill.
function layoutLegendBoxLines(doc, items, maxWidth) {
  doc.setFontSize(8);
  doc.setFont(undefined, "normal");
  const itemWidths = items.map((it) => BOX_LEGEND_SWATCH + 6 + doc.getTextWidth(it.label));

  const lines = [];
  let current = [];
  let currentW = 0;
  items.forEach((it, i) => {
    const w = itemWidths[i];
    const added = current.length ? w + BOX_LEGEND_ITEM_GAP : w;
    if (current.length && currentW + added > maxWidth) {
      lines.push({ items: current, width: currentW });
      current = [];
      currentW = 0;
    }
    current.push({ item: it, w });
    currentW += current.length > 1 ? w + BOX_LEGEND_ITEM_GAP : w;
  });
  if (current.length) lines.push({ items: current, width: currentW });
  return lines;
}

// Alto real que va a ocupar la caja — para reservarle espacio al gráfico de
// arriba antes de dibujarlo, en vez de un alto fijo adivinado.
function measureLegendBoxHeight(doc, items, maxWidth) {
  const lines = layoutLegendBoxLines(doc, items, maxWidth);
  return lines.length * BOX_LEGEND_ROW_H + (lines.length - 1) * BOX_LEGEND_ROW_GAP + BOX_LEGEND_PAD_Y * 2;
}

function drawLegendBox(doc, items, x, y, w, h) {
  const lines = layoutLegendBoxLines(doc, items, w - BOX_LEGEND_PAD_X * 2);

  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.6);
  doc.roundedRect(x, y, w, h, 6, 6, "FD");

  lines.forEach((line, lineIdx) => {
    let cursorX = x + (w - line.width) / 2;
    const cy = y + BOX_LEGEND_PAD_Y + lineIdx * (BOX_LEGEND_ROW_H + BOX_LEGEND_ROW_GAP) + BOX_LEGEND_ROW_H / 2;
    line.items.forEach(({ item, w: itemW }) => {
      doc.setFillColor(...hexToRgb(item.color));
      doc.roundedRect(cursorX, cy - BOX_LEGEND_SWATCH / 2, BOX_LEGEND_SWATCH, BOX_LEGEND_SWATCH, 1.5, 1.5, "F");
      doc.setTextColor(55, 65, 81);
      doc.setFont(undefined, "normal");
      doc.text(item.label, cursorX + BOX_LEGEND_SWATCH + 6, cy + 2.8);
      cursorX += itemW + BOX_LEGEND_ITEM_GAP;
    });
  });
}

// Dibuja una tarjeta de gráfico (fondo + título + gráfico + leyenda) en la
// posición y tamaño indicados. `legendPosition`: "bottom" (default, pills
// debajo — gráficos de barra con leyenda nativa G2) o "box" (caja de
// cuadraditos + etiqueta, en fila, centrada debajo — donas, mismo estilo
// que <ChartLegend> en pantalla pero abajo en vez de al costado).
export async function drawChartCard(doc, { x, y, width, height, title, svgEl, image, legendItems, isEmpty, singleSliceColor, singleSlicePercent, naturalW, naturalH, emptyMessage = "Sin datos", legendPosition = "bottom" }) {
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(228, 231, 236); // mismo gris de borde que .adb__search/.cldash__search-input en pantalla
  doc.setLineWidth(0.75);
  doc.roundedRect(x, y, width, height, 8, 8, "FD");

  const pad = 10;
  doc.setFontSize(10);
  doc.setFont(undefined, "bold");
  doc.setTextColor(20, 40, 80);
  doc.text(title, x + pad, y + pad + 8);

  if (isEmpty || (!svgEl && !singleSliceColor && !image)) {
    doc.setFontSize(9);
    doc.setFont(undefined, "normal");
    doc.setTextColor(150, 150, 150);
    doc.text(emptyMessage, x + width / 2, y + height / 2, { align: "center" });
    return;
  }

  const hasBoxLegend = (legendPosition === "right" || legendPosition === "box") && legendItems && legendItems.length;
  const chartTop = y + pad + 22;
  const chartAvailW = width - pad * 2;
  const boxLegendGap = 8;
  const legendH = legendItems && legendItems.length
    ? (hasBoxLegend
        ? measureLegendBoxHeight(doc, legendItems, chartAvailW) + boxLegendGap
        : measureLegendHeight(doc, legendItems, chartAvailW) + 8)
    : 0;
  const chartAvailH = height - (chartTop - y) - pad - legendH;

  const drawLegendIfAny = (chartBottomY) => {
    if (!legendItems || !legendItems.length) return;
    if (hasBoxLegend) {
      const boxH = measureLegendBoxHeight(doc, legendItems, chartAvailW);
      drawLegendBox(doc, legendItems, x + pad, chartBottomY + boxLegendGap, chartAvailW, boxH);
    } else {
      drawLegendPills(doc, legendItems, x + pad, chartBottomY + 8, width - pad * 2);
    }
  };

  // Imagen ya capturada con html2canvas (ej. una lista de progreso en
  // HTML/CSS) en vez de un gráfico G2 — mismo tratamiento de tarjeta
  // (fondo+título) pero pegando la imagen escalada en vez de un SVG.
  if (image) {
    const scale = Math.min(chartAvailW / image.width, chartAvailH / image.height);
    const imgW = image.width * scale;
    const imgH = image.height * scale;
    const imgX = x + pad + (chartAvailW - imgW) / 2;
    doc.addImage(image.toDataURL("image/png"), "PNG", imgX, chartTop, imgW, imgH);
    return;
  }

  if (singleSliceColor) {
    const chartSize = Math.max(40, Math.min(chartAvailW, chartAvailH));
    const chartX = x + pad + (chartAvailW - chartSize) / 2;
    const outerR = chartSize / 2;
    const innerR = outerR * 0.6;
    const cx = chartX + outerR;
    const cy = chartTop + outerR;
    doc.setFillColor(...hexToRgb(singleSliceColor));
    doc.circle(cx, cy, outerR, "F");
    doc.setFillColor(255, 255, 255);
    doc.circle(cx, cy, innerR, "F");

    // Única categoría = 100% del total — misma línea + porcentaje "spider"
    // que las donas con varias categorías, apuntando hacia arriba a la
    // derecha del anillo (ángulo fijo, no hay arco real que medir acá).
    if (singleSlicePercent != null) {
      const midR = (outerR + innerR) / 2;
      const angle = -Math.PI / 4;
      const sx = cx + Math.cos(angle) * midR;
      const sy = cy + Math.sin(angle) * midR;
      const ex = cx + Math.cos(angle) * (outerR + 14);
      const ey = cy + Math.sin(angle) * (outerR + 14);
      doc.setDrawColor(120, 120, 120);
      doc.setLineWidth(0.5);
      doc.line(sx, sy, ex, ey);
      doc.setFontSize(8);
      doc.setFont(undefined, "bold");
      doc.setTextColor(60, 60, 60);
      doc.text(`${singleSlicePercent.toFixed(1)}%`, ex + 2, ey);
    }

    drawLegendIfAny(chartTop + chartSize);
    return;
  }

  // Escala respetando la proporción real con la que se renderizó el SVG en
  // vez de forzar un cuadrado — forzarlo distorsionaba/recortaba las barras.
  const scale = Math.min(chartAvailW / naturalW, chartAvailH / naturalH);
  const chartW = naturalW * scale;
  const chartH = naturalH * scale;
  const chartX = x + pad + (chartAvailW - chartW) / 2;

  // Si svg2pdf.js falla al incrustar el SVG (ej. un elemento no soportado),
  // se ve un aviso de error en el PDF en vez de una tarjeta en blanco sin
  // ninguna pista de qué pasó.
  try {
    await doc.svg(svgEl, { x: chartX, y: chartTop, width: chartW, height: chartH });
  } catch (err) {
    console.error("Error al incrustar gráfico SVG en el PDF:", err);
    doc.setFontSize(8);
    doc.setFont(undefined, "normal");
    doc.setTextColor(200, 60, 60);
    doc.text("Error al renderizar el gráfico", x + width / 2, chartTop + chartAvailH / 2, { align: "center" });
    return;
  }

  drawLegendIfAny(chartTop + chartH);
}

// Dibuja una sección de ancho completo SIN tarjeta gris: título + raya negra
// debajo, con la leyenda de pills arriba del gráfico (no abajo, como en
// drawChartCard). Para gráficos que van a ancho completo en vez de en una
// grilla de columnas (ej. DualAxes por sitio de AccidentsDashboard).
export async function drawChartSection(doc, { x, y, width, height, title, svgEl, legendItems, isEmpty, emptyMessage = "Sin datos" }) {
  doc.setFontSize(12);
  doc.setFont(undefined, "bold");
  doc.setTextColor(20, 40, 80);
  doc.text(title, x, y + 10);

  doc.setDrawColor(228, 231, 236);
  doc.setLineWidth(0.75);
  doc.line(x, y + 16, x + width, y + 16);

  let cursorY = y + 16 + 12;

  if (isEmpty || !svgEl) {
    doc.setFontSize(9);
    doc.setFont(undefined, "normal");
    doc.setTextColor(150, 150, 150);
    doc.text(emptyMessage, x + width / 2, y + height / 2, { align: "center" });
    return;
  }

  if (legendItems && legendItems.length) {
    const legendH = drawLegendPills(doc, legendItems, x, cursorY, width);
    cursorY += legendH + 10;
  }

  const chartW = width;
  const chartH = y + height - cursorY;
  const chartX = x;

  try {
    await doc.svg(svgEl, { x: chartX, y: cursorY, width: chartW, height: chartH });
  } catch (err) {
    console.error("Error al incrustar gráfico SVG en el PDF:", err);
    doc.setFontSize(9);
    doc.setFont(undefined, "normal");
    doc.setTextColor(200, 60, 60);
    doc.text("Error al renderizar el gráfico", x + width / 2, cursorY + chartH / 2, { align: "center" });
  }
}

// Acomoda N tarjetas una al lado de la otra dentro del ancho disponible.
export async function drawChartRow(doc, cards, { x, y, availW, cardH, gap = 12, emptyMessage }) {
  const cardW = (availW - gap * (cards.length - 1)) / cards.length;
  for (let i = 0; i < cards.length; i++) {
    await drawChartCard(doc, { emptyMessage, ...cards[i], x: x + i * (cardW + gap), y, width: cardW, height: cardH });
  }
}

// ── Tabla nativa (texto real, no screenshot) ────────────────────────────
//
// A diferencia de `captureElementAtWidth` (que rasteriza el HTML de la
// tabla en pantalla), esto dibuja cada celda con `doc.text`/`doc.rect`
// directo en el PDF: texto nítido y seleccionable, y el corte entre
// páginas respeta el alto de fila real en vez de cortar una imagen a
// mitad de camino. A cambio, cada dashboard tiene que declarar sus propias
// columnas (no "funciona solo" con cualquier HTML).
//
// Columna: { header, flex=1, align="left", value?: (row) => string,
//            render?: (doc, { x, y, width, height, row }) => void }
// `render` gana sobre `value` cuando ambos están (para celdas con pill/
// barra de progreso en vez de texto plano).

function resolveColumnWidths(columns, totalWidth) {
  const totalFlex = columns.reduce((s, c) => s + (c.flex || 1), 0);
  let cx = 0;
  return columns.map((c) => {
    const width = totalWidth * ((c.flex || 1) / totalFlex);
    const resolved = { ...c, width, cx };
    cx += width;
    return resolved;
  });
}

// Pill de una sola celda (ej. prioridad) — centrada en (x, y).
export function drawPill(doc, { x, y, text, color, fontSize = 7 }) {
  doc.setFontSize(fontSize);
  doc.setFont(undefined, "bold");
  const padX = 6;
  const h = 12;
  const w = doc.getTextWidth(text) + padX * 2;
  doc.setFillColor(...hexToRgb(color));
  doc.roundedRect(x - w / 2, y - h / 2, w, h, h / 2, h / 2, "F");
  doc.setTextColor(255, 255, 255);
  doc.text(text, x, y + h / 2 - 2.3, { align: "center" });
}

// Mini barra de progreso horizontal + porcentaje a la derecha.
export function drawProgressCell(doc, { x, y, width, height, pct = 0, barColor = "#1A5FA0" }) {
  const barH = 6;
  const barW = Math.max(20, width - 40);
  const barX = x + 6;
  const barY = y + height / 2 - barH / 2;
  doc.setFillColor(230, 230, 230);
  doc.roundedRect(barX, barY, barW, barH, 3, 3, "F");
  doc.setFillColor(...hexToRgb(barColor));
  doc.roundedRect(barX, barY, Math.max(2, (barW * Math.min(100, Math.max(0, pct))) / 100), barH, 3, 3, "F");
  doc.setFontSize(8);
  doc.setFont(undefined, "normal");
  doc.setTextColor(80, 80, 80);
  doc.text(`${pct}%`, x + width - 6, y + height / 2 + 3, { align: "right" });
}

function drawTableHeaderRow(doc, { x, y, width, columns, height, bg = [255, 255, 255], color = [0, 25, 54], fontSize = 8 }) {
  doc.setFillColor(...bg);
  doc.rect(x, y, width, height, "F");
  doc.setFontSize(fontSize);
  doc.setFont(undefined, "bold");
  doc.setTextColor(...color);
  columns.forEach((col) => {
    const align = col.align || "left";
    const tx = align === "center" ? x + col.cx + col.width / 2 : align === "right" ? x + col.cx + col.width - 6 : x + col.cx + 6;
    doc.text(String(col.header || "").toUpperCase(), tx, y + height / 2 + 3, { align });
  });
  // Línea debajo del encabezado — separa título de filas ahora que ambos
  // fondos son blancos (antes la franja navy del header ya cumplía esa
  // función visualmente).
  doc.setDrawColor(200, 206, 216);
  doc.setLineWidth(0.8);
  doc.line(x, y + height, x + width, y + height);
}

function drawTableDataRow(doc, { x, y, width, height, columns, row, rowIndex, fontSize = 8, zebra = false, zebraColor = [246, 246, 248] }) {
  if (zebra && rowIndex % 2 === 1) {
    doc.setFillColor(...zebraColor);
    doc.rect(x, y, width, height, "F");
  }
  columns.forEach((col) => {
    const cellX = x + col.cx;
    if (col.render) {
      col.render(doc, { x: cellX, y, width: col.width, height, row });
      return;
    }
    const align = col.align || "left";
    const value = typeof col.value === "function" ? col.value(row) : row[col.key];
    doc.setFontSize(fontSize);
    doc.setFont(undefined, "normal");
    doc.setTextColor(50, 50, 50);
    const tx = align === "center" ? cellX + col.width / 2 : align === "right" ? cellX + col.width - 6 : cellX + 6;
    const text = doc.splitTextToSize(String(value ?? "-"), col.width - 10)[0] || "-";
    doc.text(text, tx, y + height / 2 + 3, { align });
  });
  doc.setDrawColor(228, 228, 232);
  doc.setLineWidth(0.4);
  doc.line(x, y + height, x + width, y + height);
}

// Dibuja encabezado + tantas filas como quepan de `rows` (el caller ya las
// recortó a lo que entra en la página actual) y devuelve el Y final.
export function drawTablePageBlock(doc, { x, y, width, columns: rawColumns, rows, rowHeight = 20, headerHeight = 22, fontSize = 8, headerFontSize = 8, zebra = false, emptyMessage = "Sin resultados" }) {
  const columns = resolveColumnWidths(rawColumns, width);
  drawTableHeaderRow(doc, { x, y, width, columns, height: headerHeight, fontSize: headerFontSize });
  let cursorY = y + headerHeight;
  if (!rows.length) {
    doc.setFontSize(9);
    doc.setFont(undefined, "normal");
    doc.setTextColor(150, 150, 150);
    doc.text(emptyMessage, x + width / 2, cursorY + rowHeight / 2 + 3, { align: "center" });
    return cursorY + rowHeight;
  }
  rows.forEach((row, i) => {
    drawTableDataRow(doc, { x, y: cursorY, width, height: rowHeight, columns, row, rowIndex: i, fontSize, zebra });
    cursorY += rowHeight;
  });
  return cursorY;
}

// ── Orquestador de página ───────────────────────────────────────────────
//
// A diferencia de mern-consultora (donde cada dashboard arma su propio
// layout de página caso por caso), acá los 8 dashboards son mucho más
// uniformes entre sí (header + fila de KPIs + N filas/secciones de gráficos
// + tabla opcional), así que conviene una sola función que arme el
// documento completo en vez de repetir ~150 líneas de layout 8 veces.
//
// `kpiCanvas`/`tableCanvas`: canvas ya capturados por el caller (ej. con
// html2canvas) para las partes que son texto/CSS, no gráficos.
// `chartRows`: array de `{ cards, height, gap, emptyMessage }` (tarjetas en
// grilla, vía drawChartRow) o `{ section: {...} , height }` (ancho completo,
// vía drawChartSection) — cada dashboard arma el que corresponda por fila.
export async function buildStandardDashboardPdf({
  jsPDFCtor, pageTitle, companyName, generatedOnLabel, kpiCanvas, chartRows = [], tableCanvas, table, tableTitle, filename,
}) {
  const doc = new jsPDFCtor({ orientation: "landscape", unit: "pt", format: "a4" });
  const margin = 20;
  const headerH = companyName ? 68 : 56;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const availW = pageW - margin * 2;
  const pageContentH = pageH - margin - headerH;

  const addPageHeader = (titleForPage) => {
    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.setTextColor(20, 40, 80);
    doc.setCharSpace(1);
    doc.text(titleForPage, margin, 28);
    doc.setCharSpace(0);
    let dateY = 42;
    if (companyName) {
      doc.setFontSize(10);
      doc.setFont(undefined, "normal");
      doc.setTextColor(60, 80, 120);
      doc.text(companyName.toLocaleUpperCase(), margin, 42);
      dateY = 54;
    }
    doc.setFontSize(8);
    doc.setFont(undefined, "normal");
    doc.setTextColor(130, 130, 130);
    doc.text(
      `${generatedOnLabel || "Generado el"} ${new Date().toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" })}`,
      margin, dateY
    );
    doc.setDrawColor(210, 210, 210);
    doc.line(margin, dateY + 6, pageW - margin, dateY + 6);
  };

  // Número de página — abajo a la derecha, en vez de al lado del título (ahí
  // competía con el título en pantallas angostas y quedaba lejos de donde
  // un lector espera encontrar la paginación).
  const addPageFooter = (pageNum, totalPages) => {
    if (totalPages <= 1) return;
    doc.setFontSize(8);
    doc.setFont(undefined, "normal");
    doc.setTextColor(130, 130, 130);
    doc.text(`${pageNum}/${totalPages}`, pageW - margin, pageH - 10, { align: "right" });
  };

  // Recorta un canvas alto en franjas de a lo sumo una página y reparte cada
  // una en su propia hoja — para bloques (típicamente la tabla) que no
  // entran en una sola página.
  const addSlicedCanvas = (canvas, sectionTitle, isFirstPage) => {
    const pxPerPt = canvas.width / availW;
    const sliceHpx = Math.max(1, Math.floor(pageContentH * pxPerPt));
    const totalSlices = Math.max(1, Math.ceil(canvas.height / sliceHpx));

    for (let i = 0; i < totalSlices; i++) {
      if (!isFirstPage || i > 0) doc.addPage("a4", "landscape");
      addPageHeader(sectionTitle);
      addPageFooter(i + 1, totalSlices);

      const sy = i * sliceHpx;
      const sh = Math.min(sliceHpx, canvas.height - sy);
      const sliceCanvas = document.createElement("canvas");
      sliceCanvas.width = canvas.width;
      sliceCanvas.height = sh;
      sliceCanvas.getContext("2d").drawImage(canvas, 0, sy, canvas.width, sh, 0, 0, canvas.width, sh);
      const drawH = sh / pxPerPt;
      doc.addImage(sliceCanvas.toDataURL("image/png"), "PNG", margin, headerH, availW, drawH);
    }
  };

  // Las tarjetas de KPIs y los bloques de imagen (ej. una lista de progreso
  // en HTML/CSS) ya traen su propio borde por tarjeta individual (ver
  // prepareKpiCardsForPdf) — un segundo rectángulo envolviendo todo el
  // grupo quedaba como un marco duplicado. Se pega la imagen directo,
  // con un margen en blanco alrededor en vez de una tarjeta propia.
  const CARD_PAD = 8;
  const drawCanvasCard = (canvas, yPos) => {
    const imgW = availW - CARD_PAD * 2;
    const imgH = (canvas.height / canvas.width) * imgW;
    doc.addImage(canvas.toDataURL("image/png"), "PNG", margin + CARD_PAD, yPos + CARD_PAD, imgW, imgH);
  };
  const canvasCardHeight = (canvas) => ((canvas.height / canvas.width) * (availW - CARD_PAD * 2)) + CARD_PAD * 2;

  const blocks = [];
  if (kpiCanvas) {
    blocks.push({
      height: canvasCardHeight(kpiCanvas),
      canvas: kpiCanvas,
      draw: (yPos) => drawCanvasCard(kpiCanvas, yPos),
    });
  }
  chartRows.forEach((row) => {
    if (row.image) {
      // Bloque que no es un gráfico G2 (ej. una lista de barras de progreso
      // en HTML/CSS) — mismo patrón que kpiCanvas: un canvas ya capturado
      // por el caller (ej. con html2canvas) que se pega como imagen (con su
      // propio fondo de tarjeta) en el lugar que le toque según el orden de
      // `chartRows`.
      blocks.push({
        height: canvasCardHeight(row.image),
        canvas: row.image,
        title: row.title,
        draw: (yPos) => drawCanvasCard(row.image, yPos),
      });
    } else if (row.section) {
      blocks.push({
        height: row.height,
        draw: (yPos) => drawChartSection(doc, { x: margin, y: yPos, width: availW, height: row.height, ...row.section }),
      });
    } else {
      blocks.push({
        height: row.height,
        draw: (yPos) => drawChartRow(doc, row.cards, { x: margin, y: yPos, availW, cardH: row.height, gap: row.gap, emptyMessage: row.emptyMessage }),
      });
    }
  });
  if (tableCanvas) {
    blocks.push({
      height: (tableCanvas.height / tableCanvas.width) * availW,
      canvas: tableCanvas,
      title: tableTitle || pageTitle,
      draw: (yPos) => {
        const scaledH = (tableCanvas.height / tableCanvas.width) * availW;
        doc.addImage(tableCanvas.toDataURL("image/png"), "PNG", margin, yPos, availW, scaledH);
      },
    });
  }

  let cursorY = null;
  const GAP = 10;
  for (let idx = 0; idx < blocks.length; idx++) {
    const block = blocks[idx];
    const isVeryFirstBlock = idx === 0;

    if (block.height > pageContentH) {
      if (block.canvas) {
        addSlicedCanvas(block.canvas, block.title || pageTitle, isVeryFirstBlock);
      } else {
        if (!isVeryFirstBlock) doc.addPage("a4", "landscape");
        addPageHeader(pageTitle);
        await block.draw(headerH);
      }
      cursorY = null; // la próxima fila arranca hoja nueva y limpia
      continue;
    }

    const needsNewPage = cursorY === null || cursorY + block.height > pageH - margin;
    if (needsNewPage) {
      if (!isVeryFirstBlock) doc.addPage("a4", "landscape");
      addPageHeader(pageTitle);
      cursorY = headerH;
    }

    await block.draw(cursorY);
    cursorY += block.height + GAP;
  }

  // Tabla nativa (texto real, no captura de pantalla): a diferencia de
  // `tableCanvas` (una imagen que se recorta por franjas de píxeles), acá se
  // pagina por fila completa, así ninguna fila queda cortada a la mitad
  // entre una página y la siguiente.
  if (table && table.rows) {
    const { columns, rows, rowHeight = 20, headerHeight = 22, fontSize, headerFontSize, zebra, emptyMessage } = table;
    const rowsPerPage = Math.max(1, Math.floor((pageContentH - headerHeight) / rowHeight));
    const totalPages = Math.max(1, Math.ceil(rows.length / rowsPerPage));
    const title = tableTitle || pageTitle;

    for (let p = 0; p < totalPages; p++) {
      if (blocks.length > 0 || p > 0) doc.addPage("a4", "landscape");
      addPageHeader(title);
      addPageFooter(p + 1, totalPages);
      const pageRows = rows.slice(p * rowsPerPage, (p + 1) * rowsPerPage);
      drawTablePageBlock(doc, {
        x: margin, y: headerH, width: availW, columns, rows: pageRows,
        rowHeight, headerHeight, fontSize, headerFontSize, zebra, emptyMessage,
      });
    }
  }

  doc.save(filename);
}
